import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { TextField } from '@mui/material';

const LOCATION_IDENTIFIER_FORMATS = {
  LSD: '##-##-###-##-W#M',
  NTS: 'X-##-X/##-X-XX',
  UWI_LSD: '1##/##-##-###-##-W#M',
  UW_NTS: '1##/X-##-X/##-X-XX/X',
};

const ALLOWED_LOCATION_FORMATS = new Set(Object.values(LOCATION_IDENTIFIER_FORMATS));

const getFormatFromLocationType = (locationType) => {
  const normalized = String(locationType || '').toUpperCase().replace(/\s+/g, ' ').trim();

  if (normalized === 'LSD') {
    return LOCATION_IDENTIFIER_FORMATS.LSD;
  }
  if (normalized === 'NTS') {
    return LOCATION_IDENTIFIER_FORMATS.NTS;
  }
  if ((normalized.includes('UWI') || normalized.startsWith('UW')) && normalized.includes('LSD')) {
    return LOCATION_IDENTIFIER_FORMATS.UWI_LSD;
  }
  if ((normalized.includes('UWI') || normalized.startsWith('UW')) && normalized.includes('NTS')) {
    return LOCATION_IDENTIFIER_FORMATS.UW_NTS;
  }

  return '';
};

const MaskTextFieldElement = ({ config, fields, dispatchValues, setValidationError }) => {
  const { i18nLabel, Text, Field, DependentOn } = config || {};
  const selectedLocationType = fields[DependentOn]?.text || fields[DependentOn]?.name || fields[DependentOn]?.label || '';
  const rawFormat = fields[DependentOn]?.locationFormatter || '';

  const format = useMemo(() => {
    const formatFromType = getFormatFromLocationType(selectedLocationType);
    const raw = String(rawFormat || '').trim();

    // If DB already provides typed placeholders (#/X/A/9), keep it.
    if (raw.includes('#') || raw.includes('X') || raw.includes('x') || raw.includes('A') || raw.includes('a') || raw.includes('9')) {
      return raw;
    }

    // If DB uses underscore placeholders, resolve strict typed format from identifier type.
    if (raw.includes('_') && formatFromType) {
      return formatFromType;
    }

    return formatFromType || raw;
  }, [rawFormat, selectedLocationType]);

  const displayPlaceholder = useMemo(
    () => String(format || '').replace(/[Xx#9Aa]/g, '_'),
    [format]
  );

  const inputRef = useRef(null);
  const previousFormatRef = useRef(format);
  const pendingCursorRef = useRef(null);
  const preInputSelectionRef = useRef({ start: null, end: null });

  const getMaskPlaceholderType = useCallback((char) => {
    if (char === '9' || char === '#') {
      return 'digit';
    }
    if (char === 'X' || char === 'x') {
      return 'alpha';
    }
    if (char === 'A') {
      return 'upper';
    }
    if (char === 'a') {
      return 'lower';
    }
    if (char === '_') {
      return 'alnum';
    }
    return null;
  }, []);

  const isMatchingMaskType = useCallback((char, type) => {
    if (type === 'digit') {
      return /\d/.test(char);
    }
    if (type === 'upper' || type === 'lower' || type === 'alpha') {
      return /[A-Za-z]/.test(char);
    }
    if (type === 'alnum') {
      return /[A-Za-z0-9]/.test(char);
    }
    return false;
  }, []);

  const transformByMaskType = useCallback((char, type) => {
    if (type === 'upper') {
      return char.toUpperCase();
    }
    if (type === 'lower') {
      return char.toLowerCase();
    }
    if (type === 'alpha') {
      return char.toUpperCase();
    }
    if (type === 'alnum') {
      return /[A-Za-z]/.test(char) ? char.toUpperCase() : char;
    }
    return char;
  }, []);

  const findPreviousPlaceholderIndex = useCallback((maskFormat, startIndex) => {
    for (let i = startIndex; i >= 0; i -= 1) {
      if (getMaskPlaceholderType(maskFormat[i])) {
        return i;
      }
    }
    return -1;
  }, [getMaskPlaceholderType]);

  const findNextPlaceholderIndex = useCallback((maskFormat, startIndex) => {
    for (let i = startIndex; i < maskFormat.length; i += 1) {
      if (getMaskPlaceholderType(maskFormat[i])) {
        return i;
      }
    }
    return -1;
  }, [getMaskPlaceholderType]);

  const getFormatRegex = useCallback((maskFormat) => {
    if (!maskFormat) {
      return null;
    }

    const escaped = maskFormat
      .split('')
      .map((char) => {
        const type = getMaskPlaceholderType(char);
        if (type === 'digit') {
          return '\\d';
        }
        if (type === 'upper') {
          return '[A-Z]';
        }
        if (type === 'lower') {
          return '[a-z]';
        }
        if (type === 'alpha') {
          return '[A-Za-z]';
        }
        if (type === 'alnum') {
          return '[A-Za-z0-9]';
        }
        return char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      })
      .join('');

    return new RegExp(`^${escaped}$`);
  }, [getMaskPlaceholderType]);

  const isLocationIdentifierValid = useCallback((value, maskFormat, fallbackRegex) => {
    if (!value) {
      return true;
    }

    if (!ALLOWED_LOCATION_FORMATS.has(maskFormat)) {
      return true;
    }

    return fallbackRegex ? fallbackRegex.test(value) : true;
  }, []);

  const buildDisplayValue = useCallback((fieldValue, maskFormat) => {
    if (!maskFormat) {
      return fieldValue;
    }

    const maskedFromState = typeof fieldValue === 'object' && fieldValue !== null
      ? String(fieldValue.masked || '')
      : '';

    // Preserve slot positions by rendering from stored masked value first.
    if (maskedFromState) {
      let displayValue = '';
      for (let i = 0; i < maskFormat.length; i += 1) {
        const maskChar = maskFormat[i];
        const maskType = getMaskPlaceholderType(maskChar);
        const valueChar = maskedFromState[i];

        if (!maskType) {
          displayValue += maskChar;
        } else if (valueChar && valueChar !== maskChar && isMatchingMaskType(valueChar, maskType)) {
          displayValue += transformByMaskType(valueChar, maskType);
        } else {
          displayValue += '_';
        }
      }
      return displayValue;
    }

    // Backward compatibility for legacy clean/string values.
    const actualCleanValue = typeof fieldValue === 'object' && fieldValue !== null ? fieldValue.clean : fieldValue;
    const candidates = (typeof actualCleanValue === 'string' ? actualCleanValue : '').split('');

    let displayValue = '';
    let candidateIndex = 0;

    for (let i = 0; i < maskFormat.length; i += 1) {
      const char = maskFormat[i];
      const maskType = getMaskPlaceholderType(char);

      if (maskType) {
        while (candidateIndex < candidates.length && !isMatchingMaskType(candidates[candidateIndex], maskType)) {
          candidateIndex += 1;
        }

        if (candidateIndex >= candidates.length) {
          displayValue += '_';
        } else {
          displayValue += transformByMaskType(candidates[candidateIndex], maskType);
          candidateIndex += 1;
        }
      } else {
        displayValue += char;
      }
    }

    return displayValue;
  }, [getMaskPlaceholderType, isMatchingMaskType, transformByMaskType]);

  const getCursorPosition = useCallback((fieldValue, maskFormat) => {
    if (!maskFormat) {
      return 0;
    }

    const maskedFromState = typeof fieldValue === 'object' && fieldValue !== null
      ? String(fieldValue.masked || '')
      : '';

    if (maskedFromState) {
      for (let i = 0; i < maskFormat.length; i += 1) {
        const maskChar = maskFormat[i];
        const maskType = getMaskPlaceholderType(maskChar);
        const valueChar = maskedFromState[i];

        if (maskType && (!valueChar || valueChar === maskChar || !isMatchingMaskType(valueChar, maskType))) {
          return i;
        }
      }
      return maskFormat.length;
    }

    return 0;
  }, [getMaskPlaceholderType, isMatchingMaskType]);

  const extractCleanValueFromMasked = useCallback((maskedValue, maskFormat) => {
    if (!maskFormat) {
      return String(maskedValue || '');
    }

    const masked = String(maskedValue || '');
    let clean = '';

    for (let i = 0; i < maskFormat.length && i < masked.length; i += 1) {
      const maskChar = maskFormat[i];
      const maskType = getMaskPlaceholderType(maskChar);
      const valueChar = masked[i];

      if (maskType && valueChar !== maskChar && isMatchingMaskType(valueChar, maskType)) {
        clean += transformByMaskType(valueChar, maskType);
      }
    }

    return clean;
  }, [getMaskPlaceholderType, isMatchingMaskType, transformByMaskType]);

  const clearPlaceholdersInRange = useCallback((maskedChars, maskFormat, startIndex, endIndex) => {
    for (let i = Math.max(0, startIndex); i < Math.min(maskFormat.length, endIndex); i += 1) {
      if (getMaskPlaceholderType(maskFormat[i])) {
        maskedChars[i] = maskFormat[i];
      }
    }
  }, [getMaskPlaceholderType]);

  const updateMaskedValueByInputAction = useCallback((
    previousMaskedValue,
    maskFormat,
    inputType,
    inputData,
    rawCursorPos,
    preSelectionStart,
    preSelectionEnd,
  ) => {
    if (!maskFormat) {
      return { maskedValue: '', targetIndex: -1, hasSelection: false };
    }

    const previousMasked = String(previousMaskedValue || maskFormat).padEnd(maskFormat.length, '');
    const next = previousMasked.split('');

    const selectionStart = typeof preSelectionStart === 'number' ? preSelectionStart : rawCursorPos;
    const selectionEnd = typeof preSelectionEnd === 'number' ? preSelectionEnd : rawCursorPos;
    const normalizedStart = Math.max(0, selectionStart || 0);
    const normalizedEnd = Math.max(0, selectionEnd || 0);
    const hasSelection = normalizedEnd > normalizedStart;

    if (inputType === 'insertText' && typeof inputData === 'string' && inputData.length === 1) {
      if (hasSelection) {
        clearPlaceholdersInRange(next, maskFormat, normalizedStart, normalizedEnd);
      }

      const insertionBase = hasSelection ? normalizedStart : Math.max(0, normalizedStart);
      const targetIndex = findNextPlaceholderIndex(maskFormat, insertionBase);

      if (targetIndex >= 0) {
        const type = getMaskPlaceholderType(maskFormat[targetIndex]);
        if (isMatchingMaskType(inputData, type)) {
          next[targetIndex] = transformByMaskType(inputData, type);
        }
      }

      return { maskedValue: next.join(''), targetIndex, hasSelection };
    }

    if (inputType === 'deleteContentBackward') {
      if (hasSelection) {
        clearPlaceholdersInRange(next, maskFormat, normalizedStart, normalizedEnd);
        return { maskedValue: next.join(''), targetIndex: normalizedStart, hasSelection: true };
      }

      const targetIndex = findPreviousPlaceholderIndex(maskFormat, Math.max(0, normalizedStart - 1));
      if (targetIndex >= 0) {
        next[targetIndex] = maskFormat[targetIndex];
      }

      return { maskedValue: next.join(''), targetIndex, hasSelection: false };
    }

    if (inputType === 'deleteContentForward') {
      if (hasSelection) {
        clearPlaceholdersInRange(next, maskFormat, normalizedStart, normalizedEnd);
        return { maskedValue: next.join(''), targetIndex: normalizedStart, hasSelection: true };
      }

      const targetIndex = findNextPlaceholderIndex(maskFormat, normalizedStart);
      if (targetIndex >= 0) {
        next[targetIndex] = maskFormat[targetIndex];
      }

      return { maskedValue: next.join(''), targetIndex, hasSelection: false };
    }

    return {
      maskedValue: String(previousMaskedValue || maskFormat),
      targetIndex: -1,
      hasSelection,
    };
  }, [
    clearPlaceholdersInRange,
    findNextPlaceholderIndex,
    findPreviousPlaceholderIndex,
    getMaskPlaceholderType,
    isMatchingMaskType,
    transformByMaskType,
  ]);

  const setFieldValueWithMask = useCallback((
    field,
    maskFormat,
    inputType,
    inputData,
    rawCursorPos,
    previousMaskedValue,
    preSelectionStart,
    preSelectionEnd,
  ) => {
    if (!maskFormat) {
      dispatchValues(field, '');
      return;
    }

    const {
      maskedValue,
      targetIndex,
      hasSelection,
    } = updateMaskedValueByInputAction(
      previousMaskedValue,
      maskFormat,
      inputType,
      inputData,
      rawCursorPos,
      preSelectionStart,
      preSelectionEnd,
    );

    const cleanValue = extractCleanValueFromMasked(maskedValue, maskFormat);

    let nextCursorPos = typeof rawCursorPos === 'number' ? rawCursorPos : 0;

    const isInsert = inputType === 'insertText' && typeof inputData === 'string' && inputData.length === 1;

    if (isInsert) {
      if (targetIndex >= 0 && maskedValue[targetIndex] !== maskFormat[targetIndex]) {
        const nextPlaceholder = findNextPlaceholderIndex(maskFormat, targetIndex + 1);
        nextCursorPos = nextPlaceholder >= 0 ? nextPlaceholder : maskFormat.length;
      } else {
        nextCursorPos = Math.max(0, (typeof preSelectionStart === 'number' ? preSelectionStart : nextCursorPos));
      }
    }

    if (inputType === 'deleteContentBackward' || inputType === 'deleteContentForward') {
      if (targetIndex >= 0) {
        nextCursorPos = targetIndex;
      } else if (hasSelection && typeof preSelectionStart === 'number') {
        nextCursorPos = preSelectionStart;
      }
    }

    pendingCursorRef.current = Math.max(0, Math.min(nextCursorPos, maskFormat.length));

    dispatchValues(field, { clean: cleanValue, masked: maskedValue });
  }, [
    dispatchValues,
    extractCleanValueFromMasked,
    findNextPlaceholderIndex,
    updateMaskedValueByInputAction,
  ]);

  const formatRegex = useMemo(() => getFormatRegex(format), [getFormatRegex, format]);
  const currentValue = fields[Field] || '';
  // const cleanValueStr = typeof currentValue === 'object' && currentValue !== null ? currentValue.clean : currentValue;
  const displayValue = useMemo(() => buildDisplayValue(currentValue, format), [buildDisplayValue, currentValue, format]);
  // const hasInvalidFormat = !!(cleanValueStr && !isLocationIdentifierValid(displayValue, format, formatRegex));

  useEffect(() => {
    if (previousFormatRef.current !== format) {
      dispatchValues(Field, '');
      previousFormatRef.current = format;
    }
  }, [Field, dispatchValues, format]);

  useEffect(() => {
    if (!setValidationError) {
      return;
    }

    const cleanStr = typeof currentValue === 'object' && currentValue !== null ? currentValue.clean : currentValue;
    const hasCurrentValueInvalidFormat = !!(cleanStr && !isLocationIdentifierValid(displayValue, format, formatRegex));

    setValidationError(hasCurrentValueInvalidFormat);
  }, [currentValue, displayValue, format, formatRegex, isLocationIdentifierValid, setValidationError]);

  useEffect(() => {
    if (inputRef.current) {
      const cursorPos = typeof pendingCursorRef.current === 'number'
        ? pendingCursorRef.current
        : getCursorPosition(currentValue, format);
      inputRef.current.setSelectionRange(cursorPos, cursorPos);
      pendingCursorRef.current = null;
    }
  }, [currentValue, displayValue, format, getCursorPosition]);

  return (
    <TextField
      fullWidth
      label={i18nLabel || Text}
      name={Field}
      size="small"
      placeholder={displayPlaceholder}
      required={false}
      disabled={!(fields[DependentOn]?.id)}
      value={displayValue}
      // error={hasInvalidFormat}
      onKeyDown={(e) => {
        preInputSelectionRef.current = {
          start: e.target.selectionStart,
          end: e.target.selectionEnd,
        };
      }}
      onChange={(e) => setFieldValueWithMask(
        Field,
        format,
        e.nativeEvent?.inputType,
        e.nativeEvent?.data,
        e.target.selectionStart,
        typeof currentValue === 'object' && currentValue !== null ? currentValue.masked : displayValue,
        preInputSelectionRef.current?.start,
        preInputSelectionRef.current?.end,
      )}
      inputRef={inputRef}
    />
  );
};

export default MaskTextFieldElement;
